import axios from 'axios';
import ChecklistWeightings from '../ChecklistWeightings/ChecklistWeightings.vue';
import RiskQuestionnaire from '../RiskQuestionnaire/RiskQuestionnaire.vue';
import alert_mixin from '@/utils/mixins/alert_mixin';
import {
  DEFINE_RISK_CATEGORY_LEVEL,
  LIST_RISK_CATEGORY_LEVELS,
  get_request_config,
} from '@/utils/system_constants/constant.js';

const to_string_value = (value) => String(value ?? '');

const resolve_risk_level_id = (level, index) => {
  const candidate_id = Number(level?.id);
  if (Number.isFinite(candidate_id) && candidate_id > 0) {
    return candidate_id;
  }

  return Date.now() + index;
};

export default {
  name: 'RiskCategories',
  mixins: [alert_mixin],
  components: {
    ChecklistWeightings,
    RiskQuestionnaire,
  },
  data: () => ({
    active_tab: 'risk_categories',
    tabs: [
      { id: 'risk_categories', label: '1. Risk Levels', icon: 'bi-grid-3x3-gap-fill' },
      { id: 'question_assignments', label: '2. Question Assignments', icon: 'bi-link-45deg' },
      { id: 'checklist_weightings', label: '3. Checklist Weightings', icon: 'bi-sliders' },
    ],
    risk_levels: [],
    selected_level_index: 0,
  }),
  created() {
    this.fetch_risk_levels();
    this.apply_route_state();
  },
  watch: {
    '$route.query.tab'() {
      this.apply_route_state();
    },
    '$route.query.risk_framework_assessment_id'() {
      this.fetch_risk_levels();
    },
  },
  computed: {
    active_level() {
      return this.risk_levels[this.selected_level_index] || null;
    },
    active_level_validation() {
      return this.validate_risk_level(this.active_level, this.selected_level_index);
    },
    risk_level_validations() {
      return this.risk_levels.map((level, index) => this.validate_risk_level(level, index));
    },
    preview_risk_levels() {
      return this.risk_levels.filter((level) => this.has_level_content(level));
    },
    can_save_risk_levels() {
      return this.risk_levels.length > 0 && this.risk_level_validations.every((validation) => validation.valid);
    },
  },
  methods: {
    current_assessment_id() {
      const route_value = Number(this.$route?.query?.risk_framework_assessment_id);

      return Number.isFinite(route_value) && route_value > 0 ? route_value : null;
    },
    format_risk_level(level = {}, index) {
      const id = resolve_risk_level_id(level, index);
      const name = to_string_value(level?.risk_level);
      const auto_decision = to_string_value(level?.auto_decision);
      const min_score = level?.min_value ?? '';
      const max_score = level?.max_value ?? '';
      const visual_color = to_string_value(level?.color_code);

      const formatted_level = {
        id,
        name,
        auto_decision,
        min_score,
        max_score,
        visual_color,
      };

      return formatted_level;
    },

    build_risk_level_row(level, index = 0) {
      const formatted_level = this.format_risk_level(level, index);

      return formatted_level;
    },
    async fetch_risk_levels() {
      const assessment_id = this.current_assessment_id();
      if (!assessment_id) {
        this.risk_levels = [];
        this.selected_level_index = 0;
        return;
      }

      try {
        const { data } = await axios.get(
          LIST_RISK_CATEGORY_LEVELS,
          get_request_config({ params: { assessment_id } }),
        );

        const rows = data?.status === 'success' && Array.isArray(data.data) ? data.data : [];
        this.risk_levels = rows.map((level, index) => this.build_risk_level_row(level, index));
        this.selected_level_index = this.risk_levels.length ? 0 : 0;
      } catch (error) {
        this.risk_levels = [];
        this.selected_level_index = 0;
      }
    },
    apply_route_state() {
      const tab = String(this.$route?.query?.tab || 'risk_categories');
      this.active_tab = this.tabs.some((item) => item.id === tab) ? tab : 'risk_categories';
    },
    set_active_tab(tab_id) {
      this.active_tab = tab_id;
    },
    select_risk_level(index) {
      if (index < 0 || index >= this.risk_levels.length) return;
      this.selected_level_index = index;
    },
    add_risk_level() {
      this.risk_levels.push({
        id: Date.now() + this.risk_levels.length,
        name: '',
        auto_decision: '',
        min_score: '',
        max_score: '',
        visual_color: '',
      });
      this.selected_level_index = this.risk_levels.length - 1;
    },
    remove_risk_level(index) {
      if (index < 0 || index >= this.risk_levels.length) return;

      if (this.risk_levels.length <= 1) {
        this.risk_levels = [{
          id: Date.now(),
          name: '',
          auto_decision: '',
          min_score: '',
          max_score: '',
          visual_color: '',
        }];
        this.selected_level_index = 0;
        return;
      }

      this.risk_levels.splice(index, 1);
      if (this.selected_level_index >= this.risk_levels.length) {
        this.selected_level_index = this.risk_levels.length - 1;
      } else if (this.selected_level_index > index) {
        this.selected_level_index -= 1;
      }
    },
    build_level_payload(level, risk_framework_assessment_id) {
      const payload = {
        risk_framework_assessment_id,
        risk_level: String(level?.name || '')
          .replace(/\s+risk$/i, '')
          .trim(),
        min_value: Number(level?.min_score),
        max_value: Number(level?.max_score),
        color_code: String(level?.visual_color || '').trim(),
        auto_decision: String(level?.auto_decision || '').trim() || 'flag',
      };

      return payload;
    },

    has_failed_response(responses) {
      const failed_response = responses.find(
        (response) => response?.data?.status !== 'success'
      );
      return failed_response || null;
    },

    save_risk_categories() {
      if (!this.can_save_risk_levels) {
        this.show_error('Fix the highlighted issues before saving risk levels.');
        return Promise.resolve(false);
      }

      const risk_framework_assessment_id = this.current_assessment_id();

      if (!risk_framework_assessment_id) {
        this.show_error('Missing risk framework assessment id.');
        return Promise.resolve(false);
      }

      const requests = this.risk_levels.map((level) =>
        axios.post(
          DEFINE_RISK_CATEGORY_LEVEL,
          this.build_level_payload(level, risk_framework_assessment_id),
          get_request_config()
        )
      );

      return Promise.all(requests)
        .then((responses) => {
          const failed_response = responses.find(
            (response) => response?.data?.status !== 'success'
          );

          if (failed_response) {
            this.show_error(
              failed_response?.data?.message ||
                'Failed to save one or more risk levels.'
            );
            return false;
          }

          this.show_success('Risk levels saved successfully.');

          return this.fetch_risk_levels().then(() => true);
        })
        .catch((error) => {
          this.show_error(
            error?.response?.data?.message ||
              error?.response?.data?.detail ||
              error?.message ||
              'Failed to save risk levels.'
          );

          return false;
        });
    },
    has_level_content(level) {
      if (!level || typeof level !== 'object') return false;

      return ['name', 'auto_decision', 'min_score', 'max_score', 'visual_color']
        .some((key) => String(Reflect.get(level, key) ?? '').trim());
    },
    parse_score_value(value) {
      if (value === null || value === undefined || value === '') return null;

      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    },
    validate_risk_level(level, index) {
      if (!level) {
      const empty_validation_result = {
        valid: false,
        message: 'Start by adding a risk level.',
        kind: 'empty',
      };

      return empty_validation_result;
      }

      const min_score = this.parse_score_value(level.min_score);
      const max_score = this.parse_score_value(level.max_score);
      const has_min = min_score !== null;
      const has_max = max_score !== null;

      if (!has_min || !has_max) {
       const validation_result = {
        valid: false,
        message: 'Enter both a minimum and maximum score.',
        kind: 'incomplete',
      };

      return validation_result;
      }

      if (min_score > max_score) {
        const range_validation_result = {
          valid: false,
          message: 'Min score cannot be greater than Max score.',
          kind: 'range',
        };

        return range_validation_result;
      }

      const overlap_index = this.risk_levels.findIndex((other_level, other_index) => {
        if (other_index === index) return false;
        if (!this.has_level_content(other_level)) return false;

        const other_min = this.parse_score_value(other_level.min_score);
        const other_max = this.parse_score_value(other_level.max_score);
        if (other_min === null || other_max === null || other_min > other_max) return false;

        return min_score <= other_max && max_score >= other_min;
      });

      if (overlap_index >= 0) {
        const overlap_validation_result = {
          valid: false,
          message: `This range overlaps with Risk Level ${overlap_index + 1}.`,
          kind: 'overlap',
        };

        return overlap_validation_result;
      }

      const valid_validation_result = {
        valid: true,
        message: '',
        kind: 'valid',
      };
      return valid_validation_result;
      },
  },
};
